/**
 * Route tree.
 *
 * The structure is the information architecture: every module of the product is
 * its own level, so no page has to host two tasks. Guards are layout routes, and
 * the share-link branch is intentionally public apart from the actions that write
 * data.
 */

import { Navigate, Outlet, createBrowserRouter } from 'react-router'

import { AppShell } from './components/layout/app-shell'
import { RequireAuth, RequireCapability, RequireInvite } from './components/layout/guards'
import { HomePage } from './pages/home-page'
import { LoginEmailPage } from './pages/login/email-page'
import { LoginGithubPage } from './pages/login/github-page'
import { LoginVerifyPage } from './pages/login/verify-page'
import { NotFoundPage } from './pages/not-found-page'
import { RouteErrorPage } from './pages/route-error-page'
import { AboutPage, GuidePage } from './pages/static-pages'

import { MeHomePage } from './pages/me/me-home-page'
import { ProfilePage } from './pages/me/profile-page'
import { InvitePage } from './pages/me/invite-page'
import { EventsPage } from './pages/me/events-page'
import { HistoryPage } from './pages/me/history-page'
import { EventDetailLayout } from './pages/me/event-detail/layout'
import { EventDetailIndexPage } from './pages/me/event-detail/index-page'
import { EventBasicPage } from './pages/me/event-detail/basic-page'
import { EventActivitiesPage } from './pages/me/event-detail/activities-page'
import { EventParticipantsPage } from './pages/me/event-detail/participants-page'
import { EventRecommendPage } from './pages/me/event-detail/recommend-page'
import { NewEventLayout } from './pages/me/new-event/layout'
import { NewEventTitlePage } from './pages/me/new-event/title-page'
import { NewEventActivitiesPage } from './pages/me/new-event/activities-page'
import { NewEventWeatherPage } from './pages/me/new-event/weather-page'
import { NewEventReviewPage } from './pages/me/new-event/review-page'

import { AdminLayout } from './pages/admin/layout'
import { AdminAdminsPage } from './pages/admin/admins-page'
import { AdminApplicationDetailPage } from './pages/admin/application-detail-page'
import { AdminApplicationsPage } from './pages/admin/applications-page'
import { AdminCodeNewPage } from './pages/admin/code-new-page'
import { AdminCodesPage } from './pages/admin/codes-page'
import { AdminDataStatusPage } from './pages/admin/data-status-page'
import { AdminMapDataPage } from './pages/admin/map-data-page'
import { AdminOverviewPage } from './pages/admin/overview-page'
import { AdminReferralDetailPage } from './pages/admin/referral-detail-page'
import { AdminReferralsPage } from './pages/admin/referrals-page'
import { AdminSettingsPage } from './pages/admin/settings-page'
import { AdminUserDetailPage } from './pages/admin/user-detail-page'
import { AdminUsersPage } from './pages/admin/users-page'

import { SharedLayout } from './pages/shared/shared-layout'
import { SharedOverviewPage } from './pages/shared/overview-page'
import { JoinLayout } from './pages/shared/join/layout'
import { JoinLocationPage } from './pages/shared/join/location-page'
import { JoinModesPage } from './pages/shared/join/modes-page'
import { JoinTimePage } from './pages/shared/join/time-page'
import { JoinActivitiesPage } from './pages/shared/join/activities-page'
import { JoinPreferencesPage } from './pages/shared/join/preferences-page'
import { JoinConfirmPage } from './pages/shared/join/confirm-page'
import { RecommendationsPage } from './pages/shared/recommendations-page'
import { RoundDetailPage } from './pages/shared/round-detail-page'
import { VotePage } from './pages/shared/vote-page'
import { DiscussPage } from './pages/shared/discuss-page'
import { CalendarPage } from './pages/shared/calendar-page'
import { CalendarExportPage } from './pages/shared/calendar-export-page'

/**
 * Layout route that requires a session and then renders the matched child.
 *
 * Guarding at the layout level means every `/me/*` route is protected by
 * construction, including ones added later.
 */
function RequireAuthLayout() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  )
}

/**
 * Layout route for the administration console.
 *
 * The gate asks for an administrator, not for one particular capability: the
 * console has several areas and a `reviewer` holds only the request ones. Each
 * page inside is then rendered from `AdminLayout`'s navigation, which is built
 * from the caller's own capability list, and every endpoint re-checks on the
 * server regardless.
 */
function RequireAdminLayout() {
  return (
    <RequireAuth>
      <RequireCapability>
        <AdminLayout />
      </RequireCapability>
    </RequireAuth>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'guide', element: <GuidePage /> },

      {
        path: 'login',
        children: [
          { index: true, element: <Navigate to="/login/email" replace /> },
          { path: 'email', element: <LoginEmailPage /> },
          { path: 'verify', element: <LoginVerifyPage /> },
          // GitHub redirects back through the Worker, which lands the browser
          // here with a one-time ticket.
          { path: 'github', element: <LoginGithubPage /> },
        ],
      },

      {
        path: 'me',
        element: <RequireAuthLayout />,
        children: [
          { index: true, element: <MeHomePage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'invite', element: <InvitePage /> },
          { path: 'events', element: <EventsPage /> },
          { path: 'history', element: <HistoryPage /> },
          {
            path: 'events/new',
            element: (
              <RequireInvite>
                <NewEventLayout />
              </RequireInvite>
            ),
            children: [
              { index: true, element: <Navigate to="title" replace /> },
              { path: 'title', element: <NewEventTitlePage /> },
              { path: 'activities', element: <NewEventActivitiesPage /> },
              { path: 'weather', element: <NewEventWeatherPage /> },
              { path: 'review', element: <NewEventReviewPage /> },
            ],
          },
          {
            path: 'events/:id',
            element: <EventDetailLayout />,
            children: [
              { index: true, element: <EventDetailIndexPage /> },
              { path: 'basic', element: <EventBasicPage /> },
              { path: 'activities', element: <EventActivitiesPage /> },
              { path: 'participants', element: <EventParticipantsPage /> },
              { path: 'recommend', element: <EventRecommendPage /> },
            ],
          },
        ],
      },

      {
        path: 'admin',
        element: <RequireAdminLayout />,
        children: [
          { index: true, element: <AdminOverviewPage /> },
          { path: 'applications', element: <AdminApplicationsPage /> },
          { path: 'applications/:id', element: <AdminApplicationDetailPage /> },
          { path: 'codes', element: <AdminCodesPage /> },
          { path: 'codes/new', element: <AdminCodeNewPage /> },
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'users/:id', element: <AdminUserDetailPage /> },
          { path: 'referrals', element: <AdminReferralsPage /> },
          { path: 'referrals/:code', element: <AdminReferralDetailPage /> },
          { path: 'admins', element: <AdminAdminsPage /> },
          { path: 'data-status', element: <AdminDataStatusPage /> },
          { path: 'map-data', element: <AdminMapDataPage /> },
          { path: 'settings', element: <AdminSettingsPage /> },
        ],
      },

      {
        path: 'e/:shareToken',
        element: <SharedLayout />,
        children: [
          { index: true, element: <SharedOverviewPage /> },
          {
            path: 'join',
            element: <JoinLayout />,
            children: [
              { index: true, element: <Navigate to="location" replace /> },
              { path: 'location', element: <JoinLocationPage /> },
              { path: 'modes', element: <JoinModesPage /> },
              { path: 'time', element: <JoinTimePage /> },
              { path: 'activities', element: <JoinActivitiesPage /> },
              { path: 'preferences', element: <JoinPreferencesPage /> },
              { path: 'confirm', element: <JoinConfirmPage /> },
            ],
          },
          {
            path: 'recommendations',
            children: [
              { index: true, element: <RecommendationsPage /> },
              { path: ':round', element: <RoundDetailPage /> },
            ],
          },
          { path: 'vote/:round', element: <VotePage /> },
          { path: 'discuss', element: <DiscussPage /> },
          {
            path: 'calendar',
            children: [
              { index: true, element: <CalendarPage /> },
              { path: 'export', element: <CalendarExportPage /> },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
